import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, BellOff, Check, Settings } from 'lucide-react'
import { useProfile } from '../hooks/useProfile'
import { useTheme } from '../hooks/useTheme'
import { useNotifications } from '../hooks/useNotifications'
import {
  markNotificationRead,
  markAllNotificationsRead,
} from '../services/notifications'
import { NOTIFICATION_ICONS, NOTIFICATION_COLORS, getNotificationUrl } from '../utils/notificationConfig'
import type { Notification } from '../types/notifications'

// ============================================================
// COMPONENT
// ============================================================

interface NotificationsDropdownProps {
  /** 'dark' for dark-background headers (landlord sidebar, contractor header),
   *  'light' for light-background headers (tenant, landlord mobile). */
  variant?: 'dark' | 'light'
}

export default function NotificationsDropdown({ variant }: NotificationsDropdownProps) {
  const { profile } = useProfile()
  const { resolvedTheme } = useTheme()
  const navigate = useNavigate()
  const {
    unreadCount,
    notifications,
    notificationsLoaded,
    loadNotifications,
    updateNotification,
    decrementUnread,
    clearUnread,
  } = useNotifications()

  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // ----------------------------------------------------------
  // Close dropdown when clicking outside
  // ----------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // ----------------------------------------------------------
  // Handlers
  // ----------------------------------------------------------

  const handleOpen = async () => {
    const opening = !isOpen
    setIsOpen(opening)
    if (opening && !notificationsLoaded) {
      setLoading(true)
      await loadNotifications()
      setLoading(false)
    }
  }

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      try {
        await markNotificationRead(notification.id)
        updateNotification({ ...notification, is_read: true, read_at: new Date().toISOString() })
        decrementUnread()
      } catch {
        // Failed to mark as read
      }
    }

    const url = getNotificationUrl(notification.reference_type, notification.reference_id, profile?.role)
    if (url) {
      setIsOpen(false)
      navigate(url)
    }
  }

  const handleMarkRead = async (e: React.MouseEvent, notification: Notification) => {
    e.stopPropagation()
    try {
      await markNotificationRead(notification.id)
      updateNotification({ ...notification, is_read: true, read_at: new Date().toISOString() })
      decrementUnread()
    } catch {
      // Failed to mark as read
    }
  }

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead()
      clearUnread()
      // Update every notification in context to is_read=true
      notifications.forEach(n => {
        if (!n.is_read) updateNotification({ ...n, is_read: true, read_at: new Date().toISOString() })
      })
    } catch {
      // Failed to mark all as read
    }
  }

  // ----------------------------------------------------------
  // Formatting
  // ----------------------------------------------------------

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  }

  // ----------------------------------------------------------
  // Theme-aware classes
  // ----------------------------------------------------------

  // Use variant prop if provided, otherwise use system theme
  const isDark = variant ? variant === 'dark' : resolvedTheme === 'dark'

  const bellClass = isDark
    ? 'text-white/80 hover:text-white'
    : 'text-[#1a2744]/60 hover:text-[#1a2744]'

  const settingsUrl = profile?.role === 'landlord' ? '/settings/notifications' : null

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={handleOpen}
        className={`p-1.5 transition cursor-pointer ${bellClass}`}
        title="Notifications"
      >
        <span className="relative block">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 md:-top-2.5 md:-right-2.5 min-w-[16px] h-4 px-1 bg-[#c87941] text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </span>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="fixed md:absolute left-2 right-2 md:left-auto md:right-0 top-14 md:top-full md:mt-2 md:w-80 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl shadow-xl overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
            <h3 className="font-semibold text-[var(--text-primary)]" style={{ fontFamily: 'var(--font-display, "DM Sans", sans-serif)' }}>
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-[#c87941] hover:text-[#d4943f] font-medium"
                >
                  Mark all read
                </button>
              )}
              {settingsUrl && (
                <button
                  onClick={() => {
                    setIsOpen(false)
                    navigate(settingsUrl)
                  }}
                  className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition"
                  title="Notification settings"
                >
                  <Settings className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-[calc(100vh-160px)] md:max-h-96 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-[var(--text-muted)] text-sm">
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <BellOff className="w-8 h-8 text-[var(--text-muted)] mx-auto mb-2" />
                <p className="text-sm text-[var(--text-muted)] font-medium">No notifications yet</p>
                <p className="text-xs text-[var(--text-muted)] opacity-70 mt-1">
                  {"We'll notify you when something needs your attention"}
                </p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => {
                  const Icon = NOTIFICATION_ICONS[notification.type] || Bell
                  const colorClass = NOTIFICATION_COLORS[notification.type] || 'text-zinc-500 bg-zinc-50'
                  const isUnread = !notification.is_read
                  const hasLink = !!getNotificationUrl(notification.reference_type, notification.reference_id, profile?.role)

                  return (
                    <div
                      key={notification.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleNotificationClick(notification)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNotificationClick(notification) } }}
                      className={`w-full text-left px-4 py-3 border-b border-[var(--border-color)] transition ${
                        hasLink ? 'cursor-pointer' : 'cursor-default'
                      } ${isUnread ? 'bg-[var(--bg-surface)]' : 'bg-[var(--bg-card)]'} hover:bg-[var(--bg-card-hover)]`}
                    >
                      <div className="flex gap-3">
                        {/* Icon */}
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <Icon className="w-4 h-4" />
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start gap-1.5">
                            {/* Unread dot */}
                            {isUnread && (
                              <span className="w-2 h-2 rounded-full bg-[#c87941] flex-shrink-0 mt-1.5" />
                            )}
                            <p className={`text-sm truncate ${
                              isUnread
                                ? 'font-semibold text-[var(--text-primary)]'
                                : 'font-normal text-[var(--text-secondary)]'
                            }`}>
                              {notification.title}
                            </p>
                          </div>
                          <p className={`text-xs mt-0.5 ${
                            isUnread ? 'text-[var(--text-secondary)]' : 'text-[var(--text-muted)]'
                          }`}>
                            {notification.message}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-xs text-[var(--text-muted)]">
                              {formatTime(notification.created_at)}
                            </span>
                            {isUnread && (
                              <button
                                onClick={(e) => handleMarkRead(e, notification)}
                                className="text-xs text-[#c87941] hover:text-[#d4943f] flex items-center gap-1"
                              >
                                <Check className="w-3 h-3" />
                                Read
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Footer - settings link (landlords only) */}
          {settingsUrl && (
            <button
              onClick={() => {
                setIsOpen(false)
                navigate(settingsUrl)
              }}
              className="block w-full px-4 py-3 text-center text-sm text-[var(--color-accent)] hover:bg-[var(--bg-surface)] border-t border-[var(--border-color)] transition font-medium"
            >
              Manage notification settings
            </button>
          )}
        </div>
      )}
    </div>
  )
}
